import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Center, Spinner } from '@chakra-ui/react';
import { useAuth } from './AuthProvider';

/**
 * Auth gate for protected routes. Unlike the carousel-only OnboardingGuard, this
 * actually requires an authenticated session (email-OTP or OIDC): unauthenticated
 * visitors are redirected into the in-app sign-in screen (preserving the target
 * path as `state.from`), so the API never receives anonymous calls behind the app
 * shell.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, authUser, loading, signIn } = useAuth();
  const location = useLocation();
  const isAuthenticated = !!authUser || (!!user && !user.expired);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      void signIn(location.pathname + location.search);
    }
  }, [loading, isAuthenticated, signIn, location.pathname, location.search]);

  if (loading || !isAuthenticated) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="brand.500" thickness="3px" />
      </Center>
    );
  }

  return <>{children}</>;
}

export default RequireAuth;
